import React, { useEffect, useRef, useState } from "react";

/*
 * Device frames that embed a live scaled preview of our own site routes.
 * `useAutoScale` measures the container width and computes the scale factor
 * so the iframe's "virtual viewport" (e.g. 1440px) is scaled to perfectly
 * fill the container width — no white gaps, no cropping.
 */

function useAutoScale(virtualWidth) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.4);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / virtualWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [virtualWidth]);

  return { ref, scale };
}

function ScaledIframe({ src, title, virtualWidth, virtualHeight }) {
  const { ref, scale } = useAutoScale(virtualWidth);
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);
  const absoluteSrc = origin ? `${origin}${src}` : src;
  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden bg-white"
      style={{ height: virtualHeight * scale }}
    >
      {origin && (
        <iframe
          title={title}
          src={absoluteSrc}
          loading="lazy"
          scrolling="no"
          tabIndex={-1}
          className="border-0 absolute top-0 left-0"
          style={{
            width: virtualWidth,
            height: virtualHeight,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}

export function LaptopFrame({ src, title = "Preview" }) {
  return (
    <div className="w-full select-none">
      <div className="bg-[#1a1a1a] rounded-t-xl p-2.5 shadow-2xl">
        <div className="rounded-md overflow-hidden">
          <ScaledIframe
            src={src}
            title={title}
            virtualWidth={1440}
            virtualHeight={900}
          />
        </div>
      </div>
      {/* Base */}
      <div className="relative">
        <div className="h-3 bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-b-xl mx-auto w-[102%] -translate-x-[1%]">
          <div className="absolute left-1/2 -translate-x-1/2 top-1 w-20 h-1 rounded-full bg-[#0d0d0d]" />
        </div>
      </div>
    </div>
  );
}

export function MonitorFrame({ src, title = "Preview" }) {
  return (
    <div className="w-full select-none">
      <div className="bg-[#1a1a1a] rounded-lg p-2 shadow-2xl">
        <div className="rounded overflow-hidden">
          <ScaledIframe
            src={src}
            title={title}
            virtualWidth={1920}
            virtualHeight={1080}
          />
        </div>
      </div>
      {/* Stand */}
      <div className="flex flex-col items-center">
        <div className="w-2 h-6 bg-[#1a1a1a]" />
        <div className="w-28 h-2 bg-[#1a1a1a] rounded-full" />
      </div>
    </div>
  );
}

export function TabletFrame({ src, title = "Preview" }) {
  return (
    <div className="w-full max-w-[380px] mx-auto select-none">
      <div className="relative bg-[#1a1a1a] rounded-[24px] p-3 shadow-2xl">
        <div className="absolute top-1/2 -translate-y-1/2 -left-[3px] w-1 h-10 bg-[#0d0d0d] rounded-l" />
        <div className="rounded-lg overflow-hidden">
          <ScaledIframe
            src={src}
            title={title}
            virtualWidth={820}
            virtualHeight={1180}
          />
        </div>
      </div>
    </div>
  );
}

export function PhoneFrame({ src, title = "Preview" }) {
  return (
    <div className="w-full max-w-[240px] mx-auto select-none">
      <div className="relative bg-[#0d0d0d] rounded-[32px] p-2 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-4 bg-[#0d0d0d] rounded-b-2xl z-10" />
        <div className="rounded-[24px] overflow-hidden">
          <ScaledIframe
            src={src}
            title={title}
            virtualWidth={390}
            virtualHeight={820}
          />
        </div>
      </div>
    </div>
  );
}
